/*!
 * c2.when — client-side counterpart to the `when`/`how_long_ago` Django
 * template filter (see v5/common/elements/when.html). Renders a datetime the
 * same way that template does — a visually-hidden ISO span for sorting, and
 * a tooltip-bearing date/time span — but also has functions for rendering the user's local time.
 *
 * Built on dayjs + its relativeTime plugin for the `how_long_ago` Django,
 * and the dayjs timezone plugin
 */
(function (c2, dayjs) {
  "use strict";

  // These plugins are always bundled — extend
  // unconditionally so a missing plugin fails loudly here
  dayjs.extend(window.dayjs_plugin_relativeTime);
  // timezone plugin depends on utc, and must be extended after it.
  dayjs.extend(window.dayjs_plugin_utc);
  // RFC 2822 — matching Django's `date:'r'`, as used by the tooltip title in
  // v5/common/elements/when.html.
  var RFC2822_UTC_FORMAT = "ddd, DD MMM YYYY HH:mm:ss ZZ";

  const dateOnlyFormat = new Intl.DateTimeFormat(undefined, { dateStyle: "short" });
  const timeOnlyFormat = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });


  // Renders the visible "date-part"/"time-part" spans.
  //
  // Format precedence:
  //   1. The browser's detected language through Intl.DateTimeFormat
  //   2. Otherwise, the hardcoded DATE_FORMAT/TIME_FORMAT fallback.
  function renderDateTimeParts(d) {
    const dateStr = dateOnlyFormat.format(d.toDate());
    const  timeStr = timeOnlyFormat.format(d.toDate());

    return '<span class="date-part">' + dateStr + '</span>&nbsp;<span class="time-part text-muted">' + timeStr + '</span>';
  }

  // Renders a tooltip-bearing date/time span based off when.html so the two look consistent;
  // Must still call c2.tooltips.init() on the container after inserting the returned HTML
  function renderWithTooltip(date) {
    var d = dayjs.utc(date);
    var tooltip = "<p><i>" + d.fromNow() + "</i></p> <p>" + d.utc().format(RFC2822_UTC_FORMAT) + "</p>";
    return '<span class="text-nowrap no-tooltip-affordance" data-bs-toggle="tooltip" data-bs-html="true" data-max-width="600" ' +
      'data-bs-title="' + tooltip + '">' +
      renderDateTimeParts(d) +
      '</span>';
  }

  // Upgrades one server-rendered v5/common/elements/when.html instance: reads
  // its own ISO timestamp from the data-c2-when attribute, then re-renders
  // its date-part/time-part with the browser's localized format.
  //
  // We do this via a DOM scan (registered in init.js's document-ready
  // bootstrap, and after every AJAX request completes)
  function upgradeOne(el) {
    var iso = el.dataset.c2When;
    if (!iso) return;
    var html = renderDateTimeParts(
      dayjs.utc(iso)
    );
    el.innerHTML = html;
  }

  function upgradeAll(root) {
    var scope = root || document;
    var els = scope.querySelectorAll ? scope.querySelectorAll("[data-c2-when]") : [];
    for (var i = 0; i < els.length; i++) {
      // Isolate each element so a failure on one
      // instance must not abort upgrading the rest of the page's timestamps.
      try {
        upgradeOne(els[i]);
      } catch (e) {
        if (window.console) console.error("c2.when: failed to upgrade element", els[i], e);
      }
    }
  }

  c2.when = {
    renderDateTimeParts: function (date) {
      return renderDateTimeParts(dayjs.utc(date));
    },
    renderWithTooltip: renderWithTooltip,
    // Re-scans `root` (default: the whole document) for
    // v5/common/elements/when.html instances and localizes them. Safe to
    // call repeatedly — re-renders each match from its own ISO timestamp, so
    // it's idempotent, not just a one-time "already done" guard.
    upgradeAll: upgradeAll
  };
  // Re-upgrade after every AJAX request completes, how most
  // `when`-rendered markup actually reaches the DOM after the initial load
  // (DataTables row redraws, jQuery .load() calls, etc.)
  if (window.jQuery) {
    window.jQuery(document).ajaxComplete(function () { upgradeAll(); });
  }
})(window.c2 = window.c2 || {}, window.dayjs);
